/**
 * Feature flags for progressive rollout.
 *
 * Each flag controls a major feature group. When disabled, the system
 * falls back to pre-upgrade behavior:
 *   - EXPERIENCE_LEVELS=false → all users forced to "advanced"
 *   - SCORE_CARD=false → analysis results use existing AnalysisContext
 *   - WALL_STREET_TEMPLATES=false → only existing task-definitions used
 *   - ESSENTIAL_HOME=false → essential users see dashboard instead of home
 *   - SIGNAL_RESONANCE=false → signal widget hidden from dashboard
 *   - QUICK_ORDER=false → trade buttons navigate to trading section
 *   - CONCIERGE=false → Concierge (AI advisor) section hidden everywhere
 *
 * Resolution order (highest priority first):
 *   1. localStorage override (per-browser dev/QA escape hatch)
 *   2. server value (admin toggle in shine_admin → DB → /config/feature-flags)
 *   3. static default below (compile-time fallback)
 *
 * This file is the flag CATALOG (source of truth for which flags EXIST + their
 * default). The server can only override a key listed here; an unknown server
 * key is ignored. The server values are fetched ONCE at boot by
 * loadRemoteFeatureFlags() (called from main.tsx before the app mounts), so a
 * non-dev can flip a whole section on/off from shine_admin without a deploy.
 *
 * Override at runtime via browser console:
 *   localStorage.setItem("ff_EXPERIENCE_LEVELS", "false");
 *   location.reload();
 */

/**
 * Compile-time defaults = the flag catalog. Keys here are the ONLY keys the
 * server can override (unknown server keys are ignored).
 *
 * 2026-05-23 EXPERIENCE_LEVELS 永久关闭: 测试系统不再分等级 — 所有用户默认 advanced
 *   模式. 未来商业化通过 credit / 积分消耗区分用量, 不通过 NAV 等级 gate UI.
 * 2026-05-23 ESSENTIAL_HOME 关闭: 不分等级 → 所有用户进 Dashboard.
 * 2026-06-21 CONCIERGE 暂时隐藏 (nav tab removed, ?section=concierge → dashboard).
 */
const FLAG_DEFAULTS = {
  EXPERIENCE_LEVELS: false,
  SCORE_CARD: true,
  WALL_STREET_TEMPLATES: true,
  ESSENTIAL_HOME: false,
  SIGNAL_RESONANCE: true,
  QUICK_ORDER: true,
  CONCIERGE: false,
} as const;
// NOTE: `CONGRESS_COPILOT` is a dynamic-convention flag (gated via
// isFlagEnabled("CONGRESS_COPILOT", true), seeded in the BE catalog) — it stays
// OUT of the typed FLAG_DEFAULTS above, like nav.*/congress_tab.* keys.

export type FeatureFlagName = keyof typeof FLAG_DEFAULTS;

/**
 * Server overrides, populated once at boot by loadRemoteFeatureFlags(). Keyed by
 * the raw flag key — covers both the typed catalog flags above AND the dynamic
 * convention keys used for section/subtab/widget visibility (nav.*,
 * portfolio_tab.*, dashboard_widget.*; see isFlagEnabled / isSectionEnabled).
 * Empty until the boot fetch resolves (and stays empty on failure → fail-open).
 */
let remoteFlags: Record<string, boolean> = {};

/**
 * Resolve ANY flag key: localStorage(`ff_<key>`) > server value > defaultValue.
 * Used for the dynamic visibility keys that are NOT in the typed catalog. The
 * default is `true` (visible) so a brand-new section/widget the server hasn't
 * been seeded for stays visible — admin opts OUT, never silently hides.
 */
export function isFlagEnabled(key: string, defaultValue = true): boolean {
  if (typeof window !== "undefined") {
    try {
      const override = localStorage.getItem(`ff_${key}`);
      if (override === "true") return true;
      if (override === "false") return false;
    } catch {
      // localStorage unavailable
    }
  }
  const remote = remoteFlags[key];
  if (typeof remote === "boolean") return remote;
  return defaultValue;
}

function flag(name: FeatureFlagName): boolean {
  // Typed catalog flag: same resolution, but the fallback is its compile-time
  // default rather than the generic "visible".
  return isFlagEnabled(name, FLAG_DEFAULTS[name]);
}

/**
 * Is a main nav SECTION visible? Concierge keeps its own typed CONCIERGE flag;
 * every other section is gated by the `nav.<section>` convention key (default
 * visible). Sections with no flag (dashboard, chat, home, my_info) stay on.
 * Shared by the nav filter, the command palette, and the navigation guards so
 * nothing can render or land on a hidden section.
 */
export function isSectionEnabled(section: string): boolean {
  if (section === "concierge") return FEATURE_FLAGS.CONCIERGE;
  return isFlagEnabled(`nav.${section}`, true);
}

/**
 * Fetch the admin-toggled flags from the public BE endpoint and apply them as
 * the server tier of the resolution order. FAIL-OPEN: any error / timeout / bad
 * shape leaves remoteFlags empty so the app keeps its compile-time defaults and
 * never blocks boot on a config outage. Only keys present in FLAG_DEFAULTS are
 * applied (the client catalog is the source of truth for which flags exist).
 *
 * Called once from main.tsx BEFORE the app module graph loads, so even
 * module-level reads of FEATURE_FLAGS (e.g. command-palette section lists) see
 * the resolved values.
 */
export async function loadRemoteFeatureFlags(timeoutMs = 1500): Promise<void> {
  if (typeof window === "undefined" || typeof fetch === "undefined") return;
  const apiBase = import.meta.env.VITE_API_URL || "http://localhost:8000";
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const res = await fetch(`${apiBase}/api/v1/config/feature-flags`, {
      signal: controller.signal,
      credentials: "include",
    });
    if (!res.ok) return;
    const body = await res.json();
    // Envelope: { error, message, data: {KEY: bool} } — unwrap, tolerate either.
    const data = (body && typeof body === "object" && "data" in body ? body.data : body) as
      | Record<string, unknown>
      | null
      | undefined;
    if (!data || typeof data !== "object") return;
    // Store EVERY boolean key the server returns — the typed catalog flags AND
    // the dynamic nav.*/portfolio_tab.*/dashboard_widget.* visibility keys. A key
    // with no consumer is simply never read (harmless); a non-boolean is skipped.
    const next: Record<string, boolean> = {};
    for (const [key, value] of Object.entries(data)) {
      if (typeof value === "boolean") next[key] = value;
    }
    remoteFlags = next;
  } catch {
    // Fail-open: keep static defaults (network error / abort / parse error).
  } finally {
    clearTimeout(timer);
  }
}

/**
 * Resolved feature flags. Each property is a GETTER so the value is computed at
 * access time (localStorage > server > default) — NOT snapshotted at import,
 * which matters because the server tier is filled in asynchronously at boot.
 */
export const FEATURE_FLAGS: { readonly [K in FeatureFlagName]: boolean } = {
  /** Master switch for the 3-level experience system (2026-05-23 永久关闭). */
  get EXPERIENCE_LEVELS() {
    return flag("EXPERIENCE_LEVELS");
  },
  /** ScoreCard rendering in analysis results. */
  get SCORE_CARD() {
    return flag("SCORE_CARD");
  },
  /** Wall Street methodology templates in Agent section. */
  get WALL_STREET_TEMPLATES() {
    return flag("WALL_STREET_TEMPLATES");
  },
  /** Essential home page (vs fallback to dashboard) — 2026-05-23 关闭. */
  get ESSENTIAL_HOME() {
    return flag("ESSENTIAL_HOME");
  },
  /** Signal resonance widget. */
  get SIGNAL_RESONANCE() {
    return flag("SIGNAL_RESONANCE");
  },
  /** Quick order overlay (vs navigate to trading section). */
  get QUICK_ORDER() {
    return flag("QUICK_ORDER");
  },
  /**
   * Concierge (AI advisor) section. When false: the nav tab is removed, a direct
   * ?section=concierge URL is normalized to the dashboard, and every
   * programmatic navigation to it is coerced to the dashboard. Toggle live from
   * shine_admin → Feature Flags, or per-browser:
   * `localStorage.setItem("ff_CONCIERGE", "true"); location.reload();`
   */
  get CONCIERGE() {
    return flag("CONCIERGE");
  },
};
